import React from 'react'
import { createLayout } from '@dboxdev/ui-components'
import { ScrollLayout } from '@dboxdev/ui-components/gsap'
import { ScrollTrigger } from 'gsap-wrapper'

import { GlobalStyle, themes, defaultTheme } from 'styles/global-styles'
import CookiesBanner from 'components/ui-elements/cookies-banner'
import Header from 'components/header'

import 'styles/fonts.css'

// Publishes the height the panels are sized with, and — the point — refuses to
// update it on a phone when only the height changed. That is the browser's own
// chrome sliding away or the soft keyboard opening, neither of which should
// re-measure the deck; a width change is a real rotation or resize and does.
const useFrozenViewportHeight = () => {
  React.useLayoutEffect(() => {
    const apply = () => {
      const height = window.innerHeight
      if (height > 0) document.documentElement.style.setProperty('--panel-h', height + 'px')
    }
    apply()

    let lastWidth = window.innerWidth
    const onResize = () => {
      const heightOnly = window.innerWidth === lastWidth
      if (heightOnly && ScrollTrigger.isTouch === 1) return
      lastWidth = window.innerWidth
      apply()
      ScrollTrigger.refresh()
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
}

// ScrollLayout builds its ScrollSmoother with `normalizeScroll`, and the normalizer
// swallows wheel and touch before they reach any element's own listeners. On the
// landing page that stopped the closing panel handing control back, and
// `allowNestedScroll` scrolled it to the bottom on the way in; on any other route it
// takes over that route's own scrolling just as bluntly. So it is off everywhere.
//
// Re-applied rather than done once, because ScrollLayout re-creates the smoother —
// and with it the normalizer — whenever it re-renders, not only on navigation. Hence
// no dependency array: this runs after every render of the layout. The call is a
// no-op when there is no normalizer to kill.
const useDisabledScrollNormalizer = () => {
  React.useEffect(() => {
    ScrollTrigger.normalizeScroll(false)
  })
}

// Runs once inside the base LayoutProvider (so it can call useLayout) and persists
// across navigation. Everything the panels need to coordinate with the chrome lives
// here: the splash gate the hero owns, and the scroll-to-form handler the Inquire
// button in the persistent header calls.
const useAppProvider = ({ location }) => {
  useFrozenViewportHeight()
  useDisabledScrollNormalizer()
  // The splash only belongs to the landing page; any other route starts settled.
  const [showSplash, setShowSplash] = React.useState(true)
  // pages/index.js registers its crossfade-to-form handler here so the header's
  // Inquire button can reach it without the two knowing about each other.
  const scrollToInquire = React.useRef(null)

  // Only the landing page is a deck of pinned panels; legal and 404 are ordinary
  // documents. Published as an attribute rather than passed down because the two
  // readers are stylesheets — the document lock in global-styles, which must not
  // reach a page that needs to scroll, and the chrome's own backdrop, which only
  // the non-deck routes need.
  React.useEffect(() => {
    if (location?.pathname !== '/') return
    const root = document.documentElement
    root.setAttribute('data-deck', '')
    return () => root.removeAttribute('data-deck')
  }, [location?.pathname])

  React.useEffect(() => {
    if (showSplash && location && location.pathname !== '/') setShowSplash(false)
  }, [location, showSplash])

  return { showSplash, setShowSplash, scrollToInquire }
}

// Builds the whole layout from a packaged shell: the base LayoutProvider (theme
// + viewport + global-style layers) wraps the shell, which owns the page
// transition. GlobalStyle is the project's Layer 3, rendered in a shell slot so
// it cascades last.
//
// Header and CookiesBanner sit in `outerBefore` — outside the ScrollSmoother
// transform, so their `position: fixed` survives, and outside the page
// transition, so they persist across navigation.
const { Layout, useAppLayout } = createLayout({
  shell: ScrollLayout,
  shellProps: {
    outerBefore: (
      <>
        <GlobalStyle />
        <Header />
        <CookiesBanner />
      </>
    )
  },
  layoutProps: {
    themes,
    defaultTheme,
    frameSizes: {
      desktop: [1280, 720],
      mobile: [393, 741]
    }
  },
  useAppProvider
})

export { useAppLayout }
export default Layout
